import { Box } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { BaseListBoxOption, ListBox, ListBoxProps } from './ListBox.component';
import { ListContainedContainer } from './ListContainedContainer.component';
import { SelectionOverview, SelectionOverviewProps } from './SelectionOverview.component';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { grey } from '@mui/material/colors';
import React, { useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { ScrollableView } from '../../Layout';


const Accordion = withStyles(MuiAccordion, (theme, _params, classes) => ({
  root: {
    width: '100%',
    boxShadow: 'none',
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.divider} !important`,
    },
    '& > .summary': {
      '&.Mui-expanded': {
        borderBottom: `1px solid ${theme.palette.divider} !important`,
      }
    },
    '&:before': {
      display: 'none',
    },
    [`&.${classes.expanded}`]: {
      margin: 'auto',
    },
  },
  expanded: {},
}));

const AccordionSummary = withStyles(MuiAccordionSummary, (_theme, _params, classes) => ({
  root: {
    minHeight: 48,
    [`&.${classes.expanded}`]: {
      minHeight: 48,
    },
  },
  content: {
    [`&.${classes.expanded}`]: {
      margin: 0,
    },
    fontWeight: 600,
  },
  expanded: {},
}));

const AccordionDetails = withStyles(MuiAccordionDetails, {
  root: {
    padding: 0,
  },
});

const SelectListGroupContext = React.createContext<{
  fieldName: string;
  expandedName: string | false;
  setExpandedName: (name: string | false) => void;
} | null>(null);

export const SelectListGroupItem = <T extends BaseListBoxOption = BaseListBoxOption>({ label, name, ...props }: ListBoxProps<T> & {
  label: React.ReactNode;
  name: string;
}) => {
  const context = React.useContext(SelectListGroupContext);

  if (!context) {
    throw new Error('SelectListGroupItem must be used within a SelectListGroup');
  }

  const { expandedName, setExpandedName } = context;
  const expanded = expandedName === name;

  return (
    <Accordion square expanded={expanded} onChange={() => setExpandedName(expanded ? false : name)}>
      <AccordionSummary className="summary" expandIcon={<ExpandMore />}>{label}</AccordionSummary>
      <AccordionDetails>
        <Box flex={1} height="260px" bgcolor={grey[100]}>
          <ListBox name={context.fieldName} {...props} disableListPadding />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};


export type SelectListGroupProps = {
  name: string;
  label?: string;
  helperText?: string;
  error?: boolean;
} & Omit<SelectionOverviewProps, 'selectedCount'>;


export const SelectListGroup: React.FC<SelectListGroupProps> = (props) => {
  const [ expanded, setExpanded ] = useState<string | false>(false);
  const formik = useFormikContext<Record<string, any>>();
  const selectedIds = formik.values[props.name];

  return (
    <SelectListGroupContext.Provider value={{
      fieldName: props.name,
      expandedName: expanded,
      setExpandedName: setExpanded,
    }}
    >
      <ListContainedContainer
        hasError={false}
        label={props.label}
        flex={false}
      >
        <ScrollableView>
          {props.children}
        </ScrollableView>
        <SelectionOverview
          selectedCount={selectedIds.length}
          selectionOverviewText={props.selectionOverviewText}
        />
      </ListContainedContainer>
    </SelectListGroupContext.Provider>
  );
};
